import { i18nHelpers } from '@/i18n/init'
import {
  __,
  append,
  complement,
  compose,
  concat,
  either,
  equals,
  filter,
  head,
  includes,
  insert,
  isEmpty,
  isNil,
  join,
  map,
  pipe,
  prepend,
  prop,
  reject,
  split,
  tail,
  toPairs,
  when,
} from 'ramda'
import { useLocation } from 'react-router-dom'
import { returns } from './function'
import { config } from '@/config'

const toId = concat(__, 'Id')

export const makeCrudPaths = (path: string, key?: string) => {
  const documentKey = key || toId(path)
  const root = `/${path}`

  return {
    root,
    view: `${root}/view/:${documentKey}`,
    viewWithKey: concat(`${root}/view/`),
    add: `${root}/add`,
    addWithKey: concat(`${root}/add/`),
    edit: `${root}/edit/:${documentKey}`,
    editWithKey: concat(`${root}/edit/`),
  }
}

// @ts-expect-error TODO fix when removing ramda
export const makeQueryString = compose(
  when(complement(isEmpty), pipe(prepend('?'), join(''))),
  join('&'), // Join each segment of the query with '&'
  map(join('=')), // Join the key-value pairs with '='
  map(map(encodeURIComponent)), // encode keys and values
  toPairs, // convert the object to pairs like `['limit', 5]`
  reject(either(isNil, isEmpty)),
)

export const queryParamsFromString = (search: string) =>
  search
    ? JSON.parse(
        `{"${search.substring(1).replace(/&/g, '","').replace(/=/g, '":"')}"}`,
        (key, value) => (key === '' ? value : decodeURIComponent(value)),
      )
    : {}

export const makeUrlWithLanguage = (
  pathname: string,
  baseUrl: string = window.location.origin,
): ((language: string) => string) =>
  (pipe(
    // @ts-expect-error TODO fix when removing ramda
    insert(1, __, [baseUrl, tail(pathname)]),
    reject(equals(i18nHelpers.fallbackLng)),
    filter(Boolean),
    join('/'),
  ) as unknown) as () => string

export const makePathnameWithLanguage = (language: string) =>
  pipe(
    tail,
    append(__, ['', language]),
    reject(equals(i18nHelpers.fallbackLng)),
    join('/'),
  )

export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const shouldRedirectToDashboard = (
  items: { locations: unknown[] }[],
) => {
  const hasMultipleItems = items.length > 1

  const firstItem = items[0]
  const locations = firstItem?.locations || []
  const firstItemHasNotExactlyOneLocation = locations.length !== 1

  return hasMultipleItems || firstItemHasNotExactlyOneLocation
}

export const makeLocationPathFromCompanies = (paths: any) => {
  // @ts-expect-error TODO fix when removing ramda
  return pipe(head, prop('locations'), head, paths.viewLocationWithKey)
}

export const goToExternalUrl = (url: string) => window.open(url, '_self')

const useRepeatWithLocale = (...initialPaths: string[]) => {
  const pathsWithLocale = [...initialPaths]

  initialPaths.forEach((path) => {
    i18nHelpers.languages.forEach((language: string) => {
      if (language !== i18nHelpers.fallbackLng) {
        pathsWithLocale.push(`/${language}${path}`)
      }
    })
  })

  return pathsWithLocale
}

export const useIsInPath = (...paths: string[]) => {
  const { pathname } = useLocation()

  return useRepeatWithLocale(...paths).includes(pathname)
}

export const withoutLanguage = pipe(
  split('/'),
  reject(includes(__, i18nHelpers.languages)),
  join('/'),
  when(isEmpty, returns('/')),
)

export const extractFunnelFromPathname = () => {
  const {
    location: { pathname },
  } = window

  const startIdx = pathname.indexOf('/assessment/') + '/assessment/'.length
  const endIdx = pathname.indexOf('/', startIdx)

  if (startIdx !== -1 && endIdx !== -1) {
    return pathname.substring(startIdx, endIdx)
  }
  return 'unknown'
}

export const checkIsInPartnerUrl = () =>
  window.location.host === config.partnerDomain
